import React, { useState, useEffect, useRef } from 'react';
import { Pencil, Save, Upload, Trash2, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/DeficiencyDetailsSlider.css';
import ImageEditor from './ImageEditor';
import RelatedDeficiencyModal from './RelatedDeficiencyModal';
import ChangeHistoryButton from './ChangeHistoryButton';
import ChangeHistoryDrawer from './ChangeHistoryDrawer';

interface Comment {
  _id: string;
  text: string;
  attachments: {
    url: string;
    filename: string;
  }[];
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

interface Deficiency {
  _id: string;
  deficiencyNumber?: number;
  projectId: string;
  project?: { _id: string; name: string } | string;
  scopeType: 'units' | 'commonArea';
  unit?: { _id: string; unitNo: string } | string;
  commonArea?: { _id: string; areaName: string; level: string } | string;
  inspectionType?: string;
  inspectionTypeName?: string;
  typeOfDeficiency?: string;
  locationCategory?: string;
  locationName?: string;
  impactLevel: string;
  priority: string;
  priorityName?: string;
  dueDate: string;
  tags: string | string[];
  title: string;
  description: string;
  attachments: Attachment[];
  isPrivate: boolean;
  status: string;
  scheduleImpact?: string;
  chargeback?: string;
  costImpact?: string;
  trade?: string;
  tradeName?: string;
  approver?: string | { _id: string; firstName: string; lastName: string; email?: string };
  approverName?: string;
  costCodes?: string;
  privateNotes?: string;
  comments?: Comment[];
  internalComments?: Comment[];
  relatedDeficiencyItems: string[];
  relatedDeficiencies?: {
    deficiency: {
      _id: string;
      deficiencyNumber: number;
      title: string;
      dueDate: string;
      status: string;
    };
    relationshipType: 'duplicate' | 'related';
  }[];
  createdAt: string;
  updatedAt: string;
  createdBy?: { _id: string; firstName: string; lastName: string } | string | null;
  createdByName?: string;
  updatedBy?: { _id: string; firstName: string; lastName: string } | string | null;
  updatedByName?: string;
  assignedDate?: string;
  completedBy?: { _id: string; firstName: string; lastName: string } | string | null;
  completedByName?: string;
  completedAt?: string;
  openedBy?: { _id: string; firstName: string; lastName: string } | string | null;
  openedByName?: string;
  openedAt?: string;
  resolvedBy?: { _id: string; firstName: string; lastName: string } | string | null;
  resolvedByName?: string;
  resolvedAt?: string;
  closedBy?: { _id: string; firstName: string; lastName: string } | string | null;
  closedByName?: string;
  closedAt?: string;
  reopenedBy?: { _id: string; firstName: string; lastName: string } | string | null;
  reopenedByName?: string;
  reopenedAt?: string;
  rejected?: boolean;
  rejectedBy?: { _id: string; firstName: string; lastName: string } | string | null;
  rejectedByName?: string;
  rejectedAt?: string;
  rejectedReason?: string;
}

interface Attachment {
  url: string;
  filename: string;
  uploadedAt: Date;
}

interface Unit {
  _id: string;
  unitNo: string;
}

interface CommonArea {
  _id: string;
  areaName: string;
  level: string;
}

interface InspectionType {
  _id: string;
  name: string;
}

interface Location {
  _id: string;
  name: string;
}

interface DeficiencyType {
  id: string;
  name: string;
}

interface Priority {
  _id: string;
  name: string;
}

interface Trade {
  _id: string;
  name: string;
}

interface Manager {
  contactId: string;
  name: string;
  email: string;
  manager: boolean;
}

interface Tag {
  _id: string;
  name: string;
}

interface Project {
  _id: string;
  name: string;
}

interface DeficiencyDetailsSliderProps {
  isOpen: boolean;
  onClose: () => void;
  deficiencyId: string;
  workspaceId: string;
  projectId: string;
  projects: Project[];
  onUpdate: () => void;
}

// Helper function to format date for HTML date input (YYYY-MM-DD)
const formatDateForInput = (dateString: string | undefined | null): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    // Format as YYYY-MM-DD for HTML date input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (err) {
    console.error('Error formatting date:', err);
    return '';
  }
};

const DeficiencyDetailsSlider: React.FC<DeficiencyDetailsSliderProps> = ({
  isOpen,
  onClose,
  deficiencyId,
  workspaceId,
  projectId,
  projects,
  onUpdate
}) => {
  const [deficiency, setDeficiency] = useState<Deficiency | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Section-specific editing states
  const [editingProjectScope, setEditingProjectScope] = useState(false);
  const [editingBasicInfo, setEditingBasicInfo] = useState(false);
  const [editingAdditionalInfo, setEditingAdditionalInfo] = useState(false);
  const [editingAttachments, setEditingAttachments] = useState(false);
  const [editingRelatedDeficiencies, setEditingRelatedDeficiencies] = useState(false);

  // Dropdown options state
  const [filteredUnits, setFilteredUnits] = useState<Unit[]>([]);
  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const justSelectedUnitRef = useRef(false);
  const [levels, setLevels] = useState<string[]>([]);
  const [commonAreas, setCommonAreas] = useState<CommonArea[]>([]);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [loadingLevels, setLoadingLevels] = useState(false);
  const [loadingCommonAreas, setLoadingCommonAreas] = useState(false);
  const [inspectionTypes, setInspectionTypes] = useState<InspectionType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [deficiencyTypes, setDeficiencyTypes] = useState<DeficiencyType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});

  // Image modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');

  // Comments state
  const [commentText, setCommentText] = useState('');
  const [commentImages, setCommentImages] = useState<File[]>([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Internal Comments state
  const [internalCommentText, setInternalCommentText] = useState('');
  const [internalCommentImages, setInternalCommentImages] = useState<File[]>([]);
  const [submittingInternalComment, setSubmittingInternalComment] = useState(false);
  const internalFileInputRef = useRef<HTMLInputElement>(null);

  // Change History state
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);

  // Loading states
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingInspectionTypes, setLoadingInspectionTypes] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingDeficiencyTypes, setLoadingDeficiencyTypes] = useState(false);
  const [loadingPriorities, setLoadingPriorities] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // Image editor state
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [currentImageFile, setCurrentImageFile] = useState<File | null>(null);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);

  // Related deficiencies modal state
  const [showRelatedDeficiencyModal, setShowRelatedDeficiencyModal] = useState(false);
  const [tempRelatedDeficiencies, setTempRelatedDeficiencies] = useState<{deficiency: string; relationshipType: 'duplicate' | 'related'; deficiencyNumber?: number; title?: string}[]>([]);

  // Track if we're currently fetching to prevent duplicate requests
  const isFetchingRef = useRef(false);
  // Use ref to store projectId to avoid recreating callback
  const projectIdRef = useRef(projectId);

  // Update ref when projectId changes
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Disable body scroll when slider is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when slider closes
      document.body.style.overflow = '';
    };
  }, []);

  // Stable data loaders
  const fetchDeficiencyDetails = React.useCallback(async () => {
    if (!workspaceId || !deficiencyId) {
      console.warn('Missing required parameters for fetching deficiency details');
      setLoading(false);
      return;
    }

    // Wait for projectId to be available
    if (!projectIdRef.current) {
      console.warn('ProjectId not yet available, waiting...');
      return;
    }

    // Prevent duplicate concurrent requests
    if (isFetchingRef.current) {
      console.log('Already fetching deficiency details, skipping...');
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      // Use projectId from ref to construct endpoint
      const endpoint = `/workspaces/${workspaceId}/${projectIdRef.current}/deficiencies/${deficiencyId}`;
      const response = await api.get(endpoint);

      const fetchedDeficiency = response.data.deficiency;
      if (fetchedDeficiency.project && typeof fetchedDeficiency.project === 'object') {
        fetchedDeficiency.projectId = fetchedDeficiency.project._id;
      } else if (fetchedDeficiency.project) {
        fetchedDeficiency.projectId = fetchedDeficiency.project;
      }
      setDeficiency(fetchedDeficiency);
    } catch (err) {
      console.error('Error fetching deficiency details:', err);
      toast.error('Failed to load deficiency details');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [workspaceId, deficiencyId]);

  const fetchUnits = React.useCallback(async (projectId: string, searchQuery?: string) => {
    if (!workspaceId || !projectId) return;
    try {
      setLoadingUnits(true);
      const response = await api.get(`/workspaces/${workspaceId}/units`, {
        params: {
          projectId: projectId,
          search: searchQuery,
          limit: 50,
          sortBy: 'unitNo',
          sortOrder: 'asc'
        }
      });
      const unitsList = response.data.units || [];
      setFilteredUnits(unitsList);
    } catch (err) {
      console.error('Error fetching units:', err);
      toast.error('Failed to load units');
      setFilteredUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  }, [workspaceId]);

  const fetchLevels = React.useCallback(async (projectId: string) => {
    if (!workspaceId || !projectId) return;
    try {
      setLoadingLevels(true);
      const response = await api.get(`/workspaces/${workspaceId}/common-areas/by-project/${projectId}/levels`);
      const levelsList = response.data.levels || [];
      setLevels(levelsList);
    } catch (err) {
      console.error('Error fetching levels:', err);
      toast.error('Failed to load levels');
      setLevels([]);
    } finally {
      setLoadingLevels(false);
    }
  }, [workspaceId]);

  const fetchCommonAreasByLevel = React.useCallback(async (projectId: string, level: string) => {
    if (!workspaceId || !projectId || !level) return;
    try {
      setLoadingCommonAreas(true);
      const response = await api.get(`/workspaces/${workspaceId}/common-areas`, {
        params: {
          projectId: projectId,
          level: level,
          status: 'ACTIVE',
          limit: 100,
          sortBy: 'areaName',
          sortOrder: 'asc'
        }
      });
      const areasData = response.data.commonAreas || [];
      setCommonAreas(areasData);
    } catch (err) {
      console.error('Error fetching common areas:', err);
      toast.error('Failed to load common areas');
      setCommonAreas([]);
    } finally {
      setLoadingCommonAreas(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (isOpen && deficiencyId) {
      setLoading(true);
      isFetchingRef.current = false; // Reset fetch flag when opening
      fetchDeficiencyDetails();
    } else if (!isOpen) {
      // Reset state when modal closes
      setDeficiency(null);
      setLoading(true);
      isFetchingRef.current = false; // Reset fetch flag when closing
    }
  }, [isOpen, deficiencyId, fetchDeficiencyDetails]);

  // Trigger fetch when projectId becomes available
  useEffect(() => {
    if (isOpen && deficiencyId && projectId && !deficiency && !isFetchingRef.current) {
      fetchDeficiencyDetails();
    }
  }, [projectId, isOpen, deficiencyId, deficiency, fetchDeficiencyDetails]);

  useEffect(() => {
    if (deficiency && deficiency.projectId) {
      fetchInspectionTypes(deficiency.projectId);
      fetchLocations(deficiency.projectId);
      fetchDeficiencyTypes(deficiency.projectId);
      fetchPriorities(deficiency.projectId);
      fetchTrades(deficiency.projectId);
      fetchManagers(deficiency.projectId);
      fetchTags(deficiency.projectId);

      // Set initial unit search query when deficiency loads
      if (deficiency.scopeType === 'units' && deficiency.unit && typeof deficiency.unit === 'object') {
        setUnitSearchQuery(deficiency.unit.unitNo);
      }

      // Set initial level and fetch common areas when deficiency loads with commonArea scope
      if (deficiency.scopeType === 'commonArea') {
        fetchLevels(deficiency.projectId);
        if (deficiency.commonArea && typeof deficiency.commonArea === 'object') {
          setSelectedLevel(deficiency.commonArea.level);
          fetchCommonAreasByLevel(deficiency.projectId, deficiency.commonArea.level);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deficiency?.projectId, isOpen]);

  // Fetch units when user searches
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // Skip if we just selected a unit
      if (justSelectedUnitRef.current) {
        justSelectedUnitRef.current = false;
        return;
      }

      // Get project ID from either projectId field or project._id if populated
      const projectIdToUse = deficiency?.projectId ||
                            (typeof deficiency?.project === 'object' ? deficiency?.project?._id : deficiency?.project);

      if (unitSearchQuery.trim().length >= 1 && projectIdToUse && editingProjectScope) {
        fetchUnits(projectIdToUse, unitSearchQuery);
      } else if (unitSearchQuery.trim().length === 0 && editingProjectScope) {
        setFilteredUnits([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounce);
  }, [unitSearchQuery, deficiency?.projectId, deficiency?.project, editingProjectScope, fetchUnits]);

  // Fetch levels when entering edit mode with commonArea scope
  useEffect(() => {
    if (editingProjectScope && deficiency?.scopeType === 'commonArea' && deficiency?.projectId) {
      const projectIdToUse = deficiency.projectId ||
                            (typeof deficiency.project === 'object' ? deficiency.project._id : deficiency.project);
      if (projectIdToUse) {
        fetchLevels(projectIdToUse);
      }
    }
  }, [editingProjectScope, deficiency?.scopeType, deficiency?.projectId, deficiency?.project, fetchLevels]);

  useEffect(() => {
    if (deficiency?.tags) {
      // Handle both string and array types for tags
      const tagsArray = typeof deficiency.tags === 'string'
        ? deficiency.tags.split(',').map(t => t.trim()).filter(t => t)
        : Array.isArray(deficiency.tags)
          ? deficiency.tags
          : [];
      setSelectedTags(tagsArray);
    }
  }, [deficiency?.tags]);

  // Fetch presigned URLs for attachments and comment attachments (incremental cache)
  useEffect(() => {
    const fetchMissingUrls = async () => {
      // Start from existing cache so we don't refetch what we already resolved
      const newMap: Record<string, string> = { ...attachmentUrls };

      // Collect all unique paths we need URLs for
      const paths = new Set<string>();

      // Main attachments
      if (deficiency?.attachments && deficiency.attachments.length > 0) {
        for (const attachment of deficiency.attachments) {
          if (!attachment.url) continue;
          paths.add(attachment.url);
        }
      }

      // Comment attachments
      if (deficiency?.comments && deficiency.comments.length > 0) {
        for (const comment of deficiency.comments) {
          if (!comment.attachments) continue;
          for (const attachment of comment.attachments) {
            if (!attachment.url) continue;
            paths.add(attachment.url);
          }
        }
      }

      // Determine which paths we still need to resolve (exclude absolute URLs)
      const toFetch = Array.from(paths).filter((p) => {
        const isAbsolute = p.startsWith('http://') || p.startsWith('https://');
        return !isAbsolute && !newMap[p];
      });

      if (toFetch.length === 0) return; // Nothing new to fetch

      // Fetch in parallel
      await Promise.all(
        toFetch.map(async (path) => {
          try {
            const response = await api.get(`/workspaces/${workspaceId}/presigned-url`, {
              params: { path }
            });
            newMap[path] = response.data.url;
          } catch (err) {
            console.error('Failed to generate presigned URL for path:', path, err);
            newMap[path] = path; // Fallback to raw path
          }
        })
      );

      // Update cache only if new entries were added
      setAttachmentUrls((prev) => {
        // Quick check: if no difference, keep previous reference to avoid re-renders
        const keysAdded = toFetch.some((k) => !prev[k]);
        return keysAdded ? { ...prev, ...newMap } : prev;
      });
    };

    fetchMissingUrls();
  }, [deficiency?.attachments, deficiency?.comments, workspaceId, attachmentUrls]);

  

  const fetchInspectionTypes = async (projectId: string) => {
    try {
      setLoadingInspectionTypes(true);
      const response = await api.get(`/service-settings/${projectId}/inspection-types`);
      const typesData = response.data.items || [];
      const typesList = typesData.map((type: any) => ({
        _id: type._id,
        name: type.name
      }));
      setInspectionTypes(typesList);
    } catch (err) {
      console.error('Error fetching inspection types:', err);
      toast.error('Failed to load inspection types');
    } finally {
      setLoadingInspectionTypes(false);
    }
  };

  const fetchLocations = async (projectId: string) => {
    if (!projectId) return;

    try {
      setLoadingLocations(true);
      const response = await api.get(`/service-settings/${projectId}/locations`);
      const locationsData = response.data.locations || response.data.items || [];
      const locationsList = locationsData.map((loc: any) => ({
        _id: loc._id,
        name: loc.name
      }));
      setLocations(locationsList);
    } catch (err: any) {
      console.error('Error fetching locations:', err);
      toast.error('Failed to load locations');
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchDeficiencyTypes = async (projectId: string) => {
    try {
      setLoadingDeficiencyTypes(true);
      const response = await api.get(`/service-settings/${projectId}/deficiency-types`);
      const typesData = response.data.items || [];
      const typesList = typesData.map((type: any) => ({
        id: type._id,
        name: type.name
      }));
      setDeficiencyTypes(typesList);
    } catch (err) {
      console.error('Error fetching deficiency types:', err);
      toast.error('Failed to load deficiency types');
    } finally {
      setLoadingDeficiencyTypes(false);
    }
  };

  const fetchPriorities = async (projectId: string) => {
    try {
      setLoadingPriorities(true);
      const response = await api.get(`/service-settings/${projectId}/priorities`);
      const prioritiesData = response.data.items || [];
      const prioritiesList = prioritiesData.map((priority: any) => ({
        _id: priority._id,
        name: priority.name
      }));
      setPriorities(prioritiesList);
    } catch (err) {
      console.error('Error fetching priorities:', err);
      toast.error('Failed to load priorities');
    } finally {
      setLoadingPriorities(false);
    }
  };

  const fetchTrades = async (projectId: string) => {
    try {
      setLoadingTrades(true);
      const response = await api.get(`/service-settings/${projectId}/trades`);
      const tradesData = response.data.items || [];
      const tradesList = tradesData.map((trade: any) => ({
        _id: trade._id,
        name: trade.name
      }));
      setTrades(tradesList);
    } catch (err) {
      console.error('Error fetching trades:', err);
      toast.error('Failed to load trades');
    } finally {
      setLoadingTrades(false);
    }
  };

  const fetchManagers = async (projectId: string) => {
    try {
      setLoadingManagers(true);
      const response = await api.get(`/service-settings/${projectId}/users`);
      const usersData = response.data.users || [];
      const managersList = usersData
        .filter((user: any) => user.manager === true)
        .map((manager: any) => ({
          contactId: manager.contactId,
          name: manager.name,
          email: manager.email,
          manager: manager.manager
        }));
      setManagers(managersList);
    } catch (err) {
      console.error('Error fetching managers:', err);
      toast.error('Failed to load managers');
    } finally {
      setLoadingManagers(false);
    }
  };

  const fetchTags = async (projectId: string) => {
    try {
      const response = await api.get(`/service-settings/${projectId}/tags`);
      const tagsData = response.data.items || [];
      const tagsList = tagsData.map((tag: any) => ({
        _id: tag._id,
        name: tag.name
      }));
      setAvailableTags(tagsList);
    } catch (err) {
      console.error('Error fetching tags:', err);
      toast.error('Failed to load tags');
    }
  };

  const handleTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTagInput(value);

    if (value.trim()) {
      const filtered = availableTags.filter(tag =>
        tag.name.toLowerCase().includes(value.toLowerCase()) &&
        !selectedTags.includes(tag.name)
      );
      setFilteredTags(filtered);
      setShowTagDropdown(true);
    } else {
      setShowTagDropdown(false);
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      addTag(tagInput.trim());
    }
  };

  const addTag = async (tagName: string) => {
    if (!selectedTags.includes(tagName)) {
      setSelectedTags([...selectedTags, tagName]);
      setTagInput('');
      setShowTagDropdown(false);

      // If tag doesn't exist in available tags, add it to service settings
      const existingTag = availableTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
      if (!existingTag && deficiency) {
        try {
          await api.post(`/service-settings/${deficiency.projectId}/tags`, { name: tagName });
          await fetchTags(deficiency.projectId);
          toast.success(`Tag "${tagName}" added to service settings`);
        } catch (err) {
          console.error('Error adding tag to service settings:', err);
        }
      }
    }
  };

  const removeTag = (index: number) => {
    const newTags = selectedTags.filter((_, i) => i !== index);
    setSelectedTags(newTags);
  };

  const selectTag = (tagName: string) => {
    addTag(tagName);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      toast.warning('Only image files are allowed');
    }

    if (imageFiles.length > 0) {
      // Open editor for the first image
      setCurrentImageFile(imageFiles[0]);
      setImageEditorOpen(true);
    }
  };

  const handleImageEditorSave = (editedFile: File) => {
    setNewAttachments(prev => [...prev, editedFile]);
    setImageEditorOpen(false);
    setCurrentImageFile(null);
    toast.success('Image added. Click Save to upload.');
  };

  const removeNewAttachment = (index: number) => {
    setNewAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdate = async () => {
    if (!deficiency) return;

    try {
      setSaving(true);

      // Prepare update data based on which section is being edited
      let updateData: any = {};

      // Project & Scope Information section
      if (editingProjectScope) {
        updateData.scopeType = deficiency.scopeType;
        if (deficiency.scopeType === 'units') {
          // Check for unitId first (set when user selects a new unit), then fall back to unit
          const unitValue = (deficiency as any).unitId || deficiency.unit;
          if (unitValue) {
            updateData.unitId = typeof unitValue === 'string' ? unitValue : unitValue._id;
          }
        } else if (deficiency.scopeType === 'commonArea') {
          // Check for commonAreaId first (set when user selects a new common area), then fall back to commonArea
          const commonAreaValue = (deficiency as any).commonAreaId || deficiency.commonArea;
          if (commonAreaValue) {
            updateData.commonAreaId = typeof commonAreaValue === 'string' ? commonAreaValue : commonAreaValue._id;
          }
        }
      }

      // Basic Information section
      if (editingBasicInfo) {
        updateData.title = deficiency.title;
        updateData.description = deficiency.description;
        updateData.status = deficiency.status;
        updateData.inspectionType = deficiency.inspectionType;
        updateData.typeOfDeficiency = deficiency.typeOfDeficiency;
        updateData.locationCategory = deficiency.locationCategory;
        updateData.impactLevel = deficiency.impactLevel;
        updateData.priority = deficiency.priority;
        updateData.dueDate = deficiency.dueDate;
        updateData.tags = selectedTags.join(', ');
      }

      // Additional Information section
      if (editingAdditionalInfo) {
        updateData.scheduleImpact = deficiency.scheduleImpact;
        updateData.costImpact = deficiency.costImpact;
        updateData.chargeback = deficiency.chargeback;
        updateData.isPrivate = deficiency.isPrivate;
        updateData.trade = deficiency.trade;
        // If approver is a populated object, extract the ID
        updateData.approver = typeof deficiency.approver === 'object' && deficiency.approver
          ? deficiency.approver._id
          : deficiency.approver;
        updateData.costCodes = deficiency.costCodes;
        updateData.privateNotes = deficiency.privateNotes;
      }

      // Attachments section
      if (editingAttachments) {
        // Upload new attachments if any
        let uploadedAttachments: any[] = [];

        if (newAttachments.length > 0) {
          toast.info(`Uploading ${newAttachments.length} image(s)...`);

          const uploadPromises = newAttachments.map(async (file) => {
            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await api.post(`/workspaces/${workspaceId}/upload/image`, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });

              // Extract path (not URL) from the response - path is permanent, URL expires
              const imagePath = response.data.image?.path || response.data.path;

              if (!imagePath) {
                console.error('No path in upload response:', response.data);
                toast.error(`Failed to get path for ${file.name}`);
                return null;
              }

              return {
                url: imagePath, // Store path, server will convert to presigned URL when needed
                filename: file.name,
                uploadedAt: new Date()
              };
            } catch (err) {
              console.error('Error uploading file:', err);
              toast.error(`Failed to upload ${file.name}`);
              return null;
            }
          });

          const results = await Promise.all(uploadPromises);
          uploadedAttachments = results.filter(item => item !== null);

          if (uploadedAttachments.length > 0) {
            toast.success(`${uploadedAttachments.length} image(s) uploaded successfully`);
          }
        }

        updateData.attachments = [...(deficiency.attachments || []), ...uploadedAttachments];
      }

      // Use deficiency's projectId for the update endpoint
      const updateProjectId = deficiency.projectId ||
                             (typeof deficiency.project === 'object' ? deficiency.project._id : deficiency.project) ||
                             projectId;

      await api.put(`/workspaces/${workspaceId}/${updateProjectId}/deficiencies/${deficiencyId}`, updateData);

      toast.success('Deficiency updated successfully');

      // Save which section was being edited before closing
      const wasEditingProjectScope = editingProjectScope;
      const currentUnitSearchQuery = unitSearchQuery;

      // Close whichever section was being edited
      setEditingProjectScope(false);
      setEditingBasicInfo(false);
      setEditingAdditionalInfo(false);
      setEditingAttachments(false);

      setNewAttachments([]);

      await fetchDeficiencyDetails();

      // Restore the unit search query after fetch if we just updated the unit
      if (wasEditingProjectScope && deficiency.scopeType === 'units' && currentUnitSearchQuery) {
        setUnitSearchQuery(currentUnitSearchQuery);
      }

      onUpdate();
    } catch (err) {
      console.error('Error updating deficiency:', err);
      toast.error('Failed to update deficiency');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (!deficiency) return;
    setDeficiency({
      ...deficiency,
      [field]: value
    });
  };

  const handleImageClick = (url: string) => {
    setSelectedImageUrl(url);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImageUrl('');
  };

  const removeAttachment = async (index: number) => {
    if (!deficiency) return;

    const confirmed = window.confirm('Are you sure you want to remove this attachment?');
    if (!confirmed) return;

    try {
      const newAttachments = deficiency.attachments.filter((_, i) => i !== index);
      const removeProjectId = deficiency.projectId || projectId;

      // Prepare data without populated fields
      const updateData = {
        ...deficiency,
        attachments: newAttachments
      };
      delete (updateData as any).unit;
      delete (updateData as any).project;

      await api.put(`/workspaces/${workspaceId}/${removeProjectId}/deficiencies/${deficiencyId}`, updateData);

      setDeficiency({
        ...deficiency,
        attachments: newAttachments
      });

      toast.success('Attachment removed');
    } catch (err) {
      console.error('Error removing attachment:', err);
      toast.error('Failed to remove attachment');
    }
  };

  const handleCommentImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newImages = Array.from(files);
      setCommentImages(prev => [...prev, ...newImages]);
    }
  };

  const removeCommentImage = (index: number) => {
    setCommentImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitComment = async () => {
    if (!deficiency || (!commentText.trim() && commentImages.length === 0)) {
      toast.error('Please enter a comment or attach an image');
      return;
    }

    try {
      setSubmittingComment(true);

      const formData = new FormData();
      formData.append('text', commentText.trim());

      commentImages.forEach(image => {
        formData.append('images', image);
      });

      const commentProjectId = deficiency.projectId ||
        (typeof deficiency.project === 'object' ? deficiency.project._id : deficiency.project) ||
        projectId;

      const response = await api.post(
        `/workspaces/${workspaceId}/${commentProjectId}/deficiencies/${deficiencyId}/comments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Add the new comment to the deficiency
      setDeficiency({
        ...deficiency,
        comments: [...(deficiency.comments || []), response.data.comment]
      });

      // Clear form
      setCommentText('');
      setCommentImages([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast.success('Comment added successfully');
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Internal Comment handlers
  const handleInternalCommentImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newImages = Array.from(files);
      setInternalCommentImages(prev => [...prev, ...newImages]);
    }
  };

  const removeInternalCommentImage = (index: number) => {
    setInternalCommentImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitInternalComment = async () => {
    if (!deficiency || (!internalCommentText.trim() && internalCommentImages.length === 0)) {
      toast.error('Please enter a comment or attach an image');
      return;
    }

    try {
      setSubmittingInternalComment(true);

      const formData = new FormData();
      formData.append('text', internalCommentText.trim());

      internalCommentImages.forEach(image => {
        formData.append('images', image);
      });

      const commentProjectId = deficiency.projectId ||
        (typeof deficiency.project === 'object' ? deficiency.project._id : deficiency.project) ||
        projectId;

      const response = await api.post(
        `/workspaces/${workspaceId}/${commentProjectId}/deficiencies/${deficiencyId}/internal-comments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Add the new internal comment to the deficiency
      setDeficiency({
        ...deficiency,
        internalComments: [...(deficiency.internalComments || []), response.data.internalComment]
      });

      // Clear form
      setInternalCommentText('');
      setInternalCommentImages([]);
      if (internalFileInputRef.current) {
        internalFileInputRef.current.value = '';
      }

      toast.success('Internal comment added successfully');
    } catch (err) {
      console.error('Error adding internal comment:', err);
      toast.error('Failed to add internal comment');
    } finally {
      setSubmittingInternalComment(false);
    }
  };

  // Comment deletion disabled by requirement (comments cannot be deleted)

  // Related Deficiencies Handlers
  const handleEditRelatedDeficiencies = () => {
    // Initialize temp state with current related deficiencies
    if (deficiency?.relatedDeficiencies) {
      setTempRelatedDeficiencies(
        deficiency.relatedDeficiencies.map(item => ({
          deficiency: item.deficiency._id,
          relationshipType: item.relationshipType,
          deficiencyNumber: item.deficiency.deficiencyNumber,
          title: item.deficiency.title
        }))
      );
    } else {
      setTempRelatedDeficiencies([]);
    }
    setEditingRelatedDeficiencies(true);
  };

  const handleSaveRelatedDeficiencies = async (newItems: {deficiency: string; relationshipType: 'duplicate' | 'related'; deficiencyNumber?: number; title?: string}[]) => {
    setTempRelatedDeficiencies(prev => [...prev, ...newItems]);
    const duplicateCount = newItems.filter(item => item.relationshipType === 'duplicate').length;
    const relatedCount = newItems.filter(item => item.relationshipType === 'related').length;

    let message = '';
    if (duplicateCount > 0 && relatedCount > 0) {
      message = `${duplicateCount} duplicate and ${relatedCount} related deficienc${relatedCount === 1 ? 'y' : 'ies'} added`;
    } else if (duplicateCount > 0) {
      message = `${duplicateCount} duplicate deficienc${duplicateCount === 1 ? 'y' : 'ies'} added`;
    } else {
      message = `${relatedCount} related deficienc${relatedCount === 1 ? 'y' : 'ies'} added`;
    }
    toast.success(message);
  };

  const handleRemoveRelatedDeficiency = (index: number) => {
    setTempRelatedDeficiencies(prev => prev.filter((_, i) => i !== index));
  };

  const handleCancelRelatedDeficienciesEdit = () => {
    setEditingRelatedDeficiencies(false);
    setTempRelatedDeficiencies([]);
  };

  const handleSaveRelatedDeficienciesChanges = async () => {
    if (!deficiency) return;

    try {
      setSaving(true);
      const updateProjectId = deficiency.projectId || projectId;

      // Prepare minimal update data with only relatedDeficiencyItems
      const updateData: any = {
        relatedDeficiencyItems: tempRelatedDeficiencies
      };

      // Copy over necessary fields from deficiency, extracting IDs from populated objects
      const fieldsToInclude = [
        '_id', 'deficiencyNumber', 'workspaceId', 'scopeType',
        'inspectionType', 'typeOfDeficiency', 'locationCategory', 'impactLevel',
        'priority', 'status', 'dueDate', 'tags', 'title', 'description',
        'attachments', 'isPrivate', 'scheduleImpact', 'chargeback', 'costImpact',
        'trade', 'costCodes', 'privateNotes'
      ];

      fieldsToInclude.forEach(field => {
        if (deficiency.hasOwnProperty(field)) {
          updateData[field] = (deficiency as any)[field];
        }
      });

      // Handle unitId/commonAreaId based on scopeType
      if (deficiency.scopeType === 'units') {
        const unitValue = (deficiency as any).unitId || deficiency.unit;
        if (unitValue) {
          updateData.unitId = typeof unitValue === 'string' ? unitValue : unitValue._id;
        }
      } else if (deficiency.scopeType === 'commonArea') {
        const commonAreaValue = (deficiency as any).commonAreaId || deficiency.commonArea;
        if (commonAreaValue) {
          updateData.commonAreaId = typeof commonAreaValue === 'string' ? commonAreaValue : commonAreaValue._id;
        }
      }

      // Extract ID from approver if it's a populated object
      if (deficiency.approver) {
        updateData.approver = typeof deficiency.approver === 'object' && deficiency.approver
          ? deficiency.approver._id
          : deficiency.approver;
      }

      await api.put(`/workspaces/${workspaceId}/${updateProjectId}/deficiencies/${deficiencyId}`, updateData);

      setEditingRelatedDeficiencies(false);
      await fetchDeficiencyDetails();
      onUpdate();

      toast.success('Related deficiencies updated successfully');
    } catch (err) {
      console.error('Error updating related deficiencies:', err);
      toast.error('Failed to update related deficiencies');
    } finally {
      setSaving(false);
    }
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMins < 1) return 'just now';
    if (diffInMins < 60) return `${diffInMins}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  // Helper function to get project name
  const getProjectName = () => {
    if (!deficiency) return '';
    // If project is populated (object), use it
    if (deficiency.project && typeof deficiency.project === 'object') {
      return deficiency.project.name;
    }
    // Otherwise look it up from projects array
    return projects.find(p => p._id === deficiency.projectId)?.name || '';
  };

  // Get presigned URL for attachment
  const getImageUrl = (url: string) => {
    // Return the presigned URL from our cache, or the original URL as fallback
    return attachmentUrls[url] || url;
  };

  return (
    <>
      <div className="deficiency-details-slider">
        <div className="deficiency-details-header">
          <div className="deficiency-details-header-left">
            <button onClick={onClose} className="deficiency-details-back-btn">
              ← Back
            </button>
            <div className="deficiency-details-title">
              <h2>
                {deficiency?.deficiencyNumber && (
                  <span style={{ color: '#48182F', marginRight: '8px' }}>#{deficiency.deficiencyNumber}</span>
                )}
                {deficiency?.title || 'Deficiency Details'}
              </h2>
              <p>
                {getProjectName()}
                {deficiency?.scopeType === 'units' && deficiency?.unit && typeof deficiency.unit === 'object' && ` • Unit: ${deficiency.unit.unitNo}`}
                {deficiency?.scopeType === 'commonArea' && deficiency?.commonArea && typeof deficiency.commonArea === 'object' && ` • ${deficiency.commonArea.areaName}`}
                {deficiency?.scopeType === 'commonArea' && (!deficiency?.commonArea || typeof deficiency.commonArea === 'string') && ' • Common Area'}
              </p>
            </div>
          </div>
          <button className="deficiency-details-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="deficiency-details-content">
          {loading ? (
            <div className="deficiency-details-loading" style={{ gridColumn: '1 / -1' }}>
              <div className="deficiency-loading-spinner" />
            </div>
          ) : !deficiency ? (
            <div className="deficiency-details-error" style={{ gridColumn: '1 / -1' }}>Deficiency not found</div>
          ) : (
            <>
              {/* Left Column */}
              <div className="deficiency-details-left">
                {/* Project and Scope */}
                <div className="deficiency-section">
                <div className="deficiency-section-header">
                  <h3 className="deficiency-section-title">Project & Scope Information</h3>
                  {!editingProjectScope && (
                    <button className="deficiency-section-edit-btn" onClick={() => setEditingProjectScope(true)}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {/* Project - Always Read-Only */}
                <div className="deficiency-detail-item">
                  <div className="deficiency-detail-label">Project</div>
                  <div className="deficiency-detail-value">
                    {getProjectName() || '-'}
                  </div>
                </div>

                {editingProjectScope ? (
                  <>
                    <div className="form-group">
                      <label className="form-label">Scope Type *</label>
                      <div style={{ display: 'flex', gap: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            value="units"
                            checked={deficiency.scopeType === 'units'}
                            onChange={(e) => {
                              handleInputChange('scopeType', e.target.value);
                              handleInputChange('commonAreaId', '');
                              setSelectedLevel('');
                              setCommonAreas([]);
                            }}
                          />
                          Units
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            value="commonArea"
                            checked={deficiency.scopeType === 'commonArea'}
                            onChange={(e) => {
                              handleInputChange('scopeType', e.target.value);
                              handleInputChange('unitId', '');
                              setUnitSearchQuery('');
                              if (deficiency.projectId) {
                                fetchLevels(deficiency.projectId);
                              }
                            }}
                          />
                          Common Area
                        </label>
                      </div>
                    </div>

                    {deficiency.scopeType === 'commonArea' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        {/* Level Dropdown */}
                        <div className="form-group">
                          <label className="form-label">Level *</label>
                          <select
                            className="form-input"
                            value={selectedLevel}
                            onChange={(e) => {
                              const level = e.target.value;
                              setSelectedLevel(level);
                              handleInputChange('commonAreaId', '');
                              if (level && deficiency.projectId) {
                                fetchCommonAreasByLevel(deficiency.projectId, level);
                              } else {
                                setCommonAreas([]);
                              }
                            }}
                            disabled={loadingLevels}
                          >
                            <option value="">
                              {loadingLevels ? 'Loading levels...' : 'Select a level'}
                            </option>
                            {levels.map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Area Dropdown */}
                        <div className="form-group">
                          <label className="form-label">Area *</label>
                          <select
                            className="form-input"
                            value={deficiency.commonArea ? (typeof deficiency.commonArea === 'string' ? deficiency.commonArea : deficiency.commonArea._id) : ''}
                            onChange={(e) => handleInputChange('commonAreaId', e.target.value)}
                            disabled={!selectedLevel || loadingCommonAreas}
                          >
                            <option value="">
                              {!selectedLevel ? 'Please select a level first' : loadingCommonAreas ? 'Loading areas...' : 'Select an area'}
                            </option>
                            {commonAreas.map((area) => (
                              <option key={area._id} value={area._id}>
                                {area.areaName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {deficiency.scopeType === 'units' && (
                      <div className="form-group">
                        <label className="form-label">Unit *</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            className="form-input"
                            value={unitSearchQuery}
                            onChange={(e) => {
                              setUnitSearchQuery(e.target.value);
                              if (!e.target.value) {
                                handleInputChange('unitId', '');
                              }
                            }}
                            placeholder="Search unit by number"
                          />
                          {loadingUnits && (
                            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                              <div className="deficiency-loading-spinner" style={{ width: '16px', height: '16px' }} />
                            </div>
                          )}
                          {unitSearchQuery && filteredUnits.length > 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              backgroundColor: 'white',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              marginTop: '4px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                              zIndex: 1000
                            }}>
                              {filteredUnits.map((unit) => (
                                <div
                                  key={unit._id}
                                  onMouseDown={(e) => {
                                    // Use onMouseDown instead of onClick to fire before input's onBlur
                                    e.preventDefault();
                                    // Set flag to prevent re-fetching after selection
                                    justSelectedUnitRef.current = true;
                                    handleInputChange('unitId', unit._id);
                                    setUnitSearchQuery(unit.unitNo);
                                    // Clear the dropdown immediately
                                    setFilteredUnits([]);
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #F3F4F6',
                                    backgroundColor: (deficiency.unit && (typeof deficiency.unit === 'string' ? deficiency.unit : deficiency.unit._id) === unit._id) ? '#F9FAFB' : 'white',
                                    transition: 'background-color 0.15s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                                  onMouseLeave={(e) => {
                                    if (!(deficiency.unit && (typeof deficiency.unit === 'string' ? deficiency.unit : deficiency.unit._id) === unit._id)) {
                                      e.currentTarget.style.backgroundColor = 'white';
                                    }
                                  }}
                                >
                                  {unit.unitNo}
                                </div>
                              ))}
                            </div>
                          )}
                          {unitSearchQuery && !loadingUnits && filteredUnits.length === 0 && !deficiency.unit && (
                            <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
                              No units found for "{unitSearchQuery}"
                            </small>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Scope Type</div>
                      <div className="deficiency-detail-value">
                        {deficiency.scopeType === 'units' ? 'Units' : 'Common Area'}
                      </div>
                    </div>

                    {deficiency.scopeType === 'units' && (
                      <div className="deficiency-detail-item">
                        <div className="deficiency-detail-label">Unit</div>
                        <div className="deficiency-detail-value">
                          {deficiency.unit && typeof deficiency.unit === 'object' ? deficiency.unit.unitNo : '-'}
                        </div>
                      </div>
                    )}

                    {deficiency.scopeType === 'commonArea' && (
                      <>
                        <div className="deficiency-detail-item">
                          <div className="deficiency-detail-label">Level</div>
                          <div className="deficiency-detail-value">
                            {deficiency.commonArea && typeof deficiency.commonArea === 'object' ? deficiency.commonArea.level : '-'}
                          </div>
                        </div>
                        <div className="deficiency-detail-item">
                          <div className="deficiency-detail-label">Area</div>
                          <div className="deficiency-detail-value">
                            {deficiency.commonArea && typeof deficiency.commonArea === 'object' ? deficiency.commonArea.areaName : '-'}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {editingProjectScope && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E5E7EB' }}>
                    <button
                      onClick={() => setEditingProjectScope(false)}
                      className="btn btn-secondary btn-sm"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdate}
                      className="btn btn-primary btn-sm"
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={16} />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              {/* Basic Information */}
              <div className="deficiency-section">
                <div className="deficiency-section-header">
                  <h3 className="deficiency-section-title">Basic Information</h3>
                  {!editingBasicInfo && (
                    <button className="deficiency-section-edit-btn" onClick={() => setEditingBasicInfo(true)}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {editingBasicInfo ? (
                  <div className="form-group">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={deficiency.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Enter deficiency title"
                    />
                  </div>
                ) : (
                  <div className="deficiency-detail-item">
                    <div className="deficiency-detail-label">Title</div>
                    <div className="deficiency-detail-value">{deficiency.title}</div>
                  </div>
                )}

                {editingBasicInfo ? (
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-input"
                      value={deficiency.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Enter description"
                      rows={4}
                    />
                  </div>
                ) : (
                  <div className="deficiency-detail-item">
                    <div className="deficiency-detail-label">Description</div>
                    <div className="deficiency-detail-value" style={{ whiteSpace: 'pre-wrap' }}>{deficiency.description || '-'}</div>
                  </div>
                )}

                {/* Status and Inspection Type */}
                {editingBasicInfo ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Status *</label>
                      <select
                        className="form-input"
                        value={deficiency.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                      >
                        <option value="open">Open</option>
                        <option value="assigned">Assigned</option>
                        <option value="accepted">Accepted</option>
                        <option value="not-accepted">Not Accepted</option>
                        <option value="completed">Completed</option>
                        <option value="closed">Closed</option>
                        <option value="reopened">Reopened</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Inspection Type</label>
                      <select
                        className="form-input"
                        value={deficiency.inspectionType || ''}
                        onChange={(e) => handleInputChange('inspectionType', e.target.value)}
                        disabled={loadingInspectionTypes}
                      >
                        <option value="">Select Inspection Type</option>
                        {/* Show current value if it exists but not in options */}
                        {deficiency.inspectionType && !inspectionTypes.find(t => t._id === deficiency.inspectionType) && deficiency.inspectionTypeName && (
                          <option value={deficiency.inspectionType}>{deficiency.inspectionTypeName}</option>
                        )}
                        {inspectionTypes.map((type) => (
                          <option key={type._id} value={type._id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {!loadingInspectionTypes && inspectionTypes.length === 0 && (
                        <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#DC2626' }}>
                          No inspection types available. Please check service settings.
                        </small>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Status</div>
                      <div className="deficiency-detail-value" style={{ textTransform: 'capitalize' }}>
                        {deficiency.status.replace('-', ' ')}
                      </div>
                    </div>

                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Inspection Type</div>
                      <div className="deficiency-detail-value">
                        {deficiency.inspectionTypeName || '-'}
                      </div>
                    </div>
                  </>
                )}

                {/* Type of Deficiency and Location */}
                {editingBasicInfo ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Type of Deficiency</label>
                      <select
                        className="form-input"
                        value={deficiency.typeOfDeficiency || ''}
                        onChange={(e) => handleInputChange('typeOfDeficiency', e.target.value)}
                        disabled={loadingDeficiencyTypes}
                      >
                        <option value="">Select Deficiency Type</option>
                        {/* Show current value if it exists but not in options */}
                        {deficiency.typeOfDeficiency && !deficiencyTypes.find(t => t.id === deficiency.typeOfDeficiency) && (
                          <option value={deficiency.typeOfDeficiency}>
                            {deficiencyTypes.find(t => t.id === deficiency.typeOfDeficiency)?.name || deficiency.typeOfDeficiency}
                          </option>
                        )}
                        {deficiencyTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {!loadingDeficiencyTypes && deficiencyTypes.length === 0 && (
                        <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#DC2626' }}>
                          No deficiency types available. Please check service settings.
                        </small>
                      )}
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Location / Category</label>
                      <select
                        className="form-input"
                        value={deficiency.locationCategory || ''}
                        onChange={(e) => handleInputChange('locationCategory', e.target.value)}
                        disabled={loadingLocations}
                      >
                        <option value="">Select Location</option>
                        {locations.map((location) => (
                          <option key={location._id} value={location._id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                      {!loadingLocations && locations.length === 0 && (
                        <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#DC2626' }}>
                          No locations available. Please check service settings.
                        </small>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Type of Deficiency</div>
                      <div className="deficiency-detail-value">
                        {deficiencyTypes.find(t => t.id === deficiency.typeOfDeficiency)?.name || deficiency.typeOfDeficiency || '-'}
                      </div>
                    </div>

                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Location / Category</div>
                      <div className="deficiency-detail-value">
                        {deficiency.locationName || locations.find(l => l._id === deficiency.locationCategory)?.name || '-'}
                      </div>
                    </div>
                  </>
                )}

                {/* Impact Level and Priority */}
                {editingBasicInfo ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Impact Level *</label>
                      <select
                        className="form-input"
                        value={deficiency.impactLevel}
                        onChange={(e) => handleInputChange('impactLevel', e.target.value)}
                      >
                        <option value="">Select Impact Level</option>
                        <option value="minor">Minor</option>
                        <option value="normal">Normal</option>
                        <option value="major">Major</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Priority *</label>
                      <select
                        className="form-input"
                        value={deficiency.priority || ''}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        disabled={loadingPriorities}
                      >
                        <option value="">Select Priority</option>
                        {/* Show current value if it exists but not in options */}
                        {deficiency.priority && !priorities.find(p => p._id === deficiency.priority) && deficiency.priorityName && (
                          <option value={deficiency.priority}>{deficiency.priorityName}</option>
                        )}
                        {priorities.map((priority) => (
                          <option key={priority._id} value={priority._id}>
                            {priority.name}
                          </option>
                        ))}
                      </select>
                      {!loadingPriorities && priorities.length === 0 && (
                        <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#DC2626' }}>
                          No priorities available. Please check service settings.
                        </small>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Impact Level</div>
                      <div className="deficiency-detail-value" style={{ textTransform: 'capitalize' }}>
                        {deficiency.impactLevel || '-'}
                      </div>
                    </div>

                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Priority</div>
                      <div className="deficiency-detail-value">
                        {deficiency.priorityName || '-'}
                      </div>
                    </div>
                  </>
                )}

                {/* Due Date and Tags */}
                {editingBasicInfo ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Due Date *</label>
                      <input
                        type="date"
                        className="form-input"
                        value={formatDateForInput(deficiency.dueDate)}
                        onChange={(e) => handleInputChange('dueDate', e.target.value)}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
                      <label className="form-label">Tags</label>
                      <div style={{ position: 'relative' }}>
                        <div
                          onClick={() => document.getElementById('tag-input-details')?.focus()}
                          style={{
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '8px',
                            minHeight: '38px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px',
                            alignItems: 'center',
                            cursor: 'text'
                          }}
                        >
                          {selectedTags.map((tag, index) => (
                            <span
                              key={index}
                              style={{
                                backgroundColor: '#e0e0e0',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '14px'
                              }}
                            >
                              {tag}
                              <button
                                onClick={() => removeTag(index)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '0 2px',
                                  fontSize: '16px',
                                  lineHeight: '1'
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <input
                            id="tag-input-details"
                            type="text"
                            value={tagInput}
                            onChange={handleTagInput}
                            onKeyDown={handleTagKeyDown}
                            placeholder={selectedTags.length === 0 ? "Type to add tags..." : ""}
                            style={{
                              border: 'none',
                              outline: 'none',
                              flex: 1,
                              minWidth: '120px',
                              padding: '4px'
                            }}
                          />
                        </div>
                        {showTagDropdown && filteredTags.length > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #ddd',
                              borderTop: 'none',
                              borderRadius: '0 0 4px 4px',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                          >
                            {filteredTags.map((tag) => (
                              <div
                                key={tag._id}
                                onClick={() => selectTag(tag.name)}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                              >
                                {tag.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Due Date</div>
                      <div className="deficiency-detail-value">
                        {deficiency.dueDate ? new Date(deficiency.dueDate).toLocaleDateString() : '-'}
                      </div>
                    </div>

                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Tags</div>
                      <div className="deficiency-detail-value">
                        {selectedTags.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {selectedTags.map((tag, index) => (
                              <span
                                key={index}
                                style={{
                                  backgroundColor: '#e0e0e0',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : '-'}
                      </div>
                    </div>
                  </>
                )}

                {editingBasicInfo && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E5E7EB' }}>
                    <button
                      onClick={() => setEditingBasicInfo(false)}
                      className="btn btn-secondary btn-sm"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdate}
                      className="btn btn-primary btn-sm"
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={16} />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
              </div>

              {/* Right Column */}
              <div className="deficiency-details-right">
                {/* Additional Information */}
                <div className="deficiency-section">
                <div className="deficiency-section-header">
                  <h3 className="deficiency-section-title">Additional Information</h3>
                  {!editingAdditionalInfo && (
                    <button className="deficiency-section-edit-btn" onClick={() => setEditingAdditionalInfo(true)}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {/* Schedule Impact and Cost Impact */}
                {editingAdditionalInfo ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Schedule Impact</label>
                      <select
                        className="form-input"
                        value={deficiency.scheduleImpact || ''}
                        onChange={(e) => handleInputChange('scheduleImpact', e.target.value)}
                      >
                        <option value="">Select Schedule Impact</option>
                        <option value="yes">Yes</option>
                        <option value="yes-unknown">Yes (Unknown)</option>
                        <option value="no">No</option>
                        <option value="not-sure">Not Sure</option>
                        <option value="n/a">N/A</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Cost Impact</label>
                      <select
                        className="form-input"
                        value={deficiency.costImpact || ''}
                        onChange={(e) => handleInputChange('costImpact', e.target.value)}
                      >
                        <option value="">Select Cost Impact</option>
                        <option value="yes">Yes</option>
                        <option value="yes-unknown">Yes (Unknown)</option>
                        <option value="no">No</option>
                        <option value="not-sure">Not Sure</option>
                        <option value="n/a">N/A</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Schedule Impact</div>
                      <div className="deficiency-detail-value" style={{ textTransform: 'capitalize' }}>
                        {deficiency.scheduleImpact || '-'}
                      </div>
                    </div>

                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Cost Impact</div>
                      <div className="deficiency-detail-value" style={{ textTransform: 'capitalize' }}>
                        {deficiency.costImpact || '-'}
                      </div>
                    </div>
                  </>
                )}

                {/* Chargeback and Private */}
                {editingAdditionalInfo ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Chargeback</label>
                      <select
                        className="form-input"
                        value={deficiency.chargeback || ''}
                        onChange={(e) => handleInputChange('chargeback', e.target.value)}
                      >
                        <option value="">Select Chargeback</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Private</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={deficiency.isPrivate}
                          onChange={(e) => handleInputChange('isPrivate', e.target.checked)}
                        />
                        Mark as private
                      </label>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Chargeback</div>
                      <div className="deficiency-detail-value" style={{ textTransform: 'capitalize' }}>
                        {deficiency.chargeback || '-'}
                      </div>
                    </div>

                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Private</div>
                      <div className="deficiency-detail-value">
                        {deficiency.isPrivate ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </>
                )}

                {/* Trade and Approver */}
                {editingAdditionalInfo ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Trade</label>
                      <select
                        className="form-input"
                        value={deficiency.trade || ''}
                        onChange={(e) => handleInputChange('trade', e.target.value)}
                        disabled={loadingTrades}
                      >
                        <option value="">Select Trade</option>
                        {trades.map((trade) => (
                          <option key={trade._id} value={trade._id}>
                            {trade.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Approver</label>
                      <select
                        className="form-input"
                        value={typeof deficiency.approver === 'object' && deficiency.approver ? deficiency.approver._id : (deficiency.approver || '')}
                        onChange={(e) => handleInputChange('approver', e.target.value)}
                        disabled={loadingManagers}
                      >
                        <option value="">Select Approver</option>
                        {/* Show current value if it exists but not in options */}
                        {deficiency.approver && typeof deficiency.approver === 'object' && (() => {
                          const approverObj = deficiency.approver as { _id: string; firstName: string; lastName: string; email?: string };
                          return !managers.find(m => m.contactId === approverObj._id) && (
                            <option value={approverObj._id}>
                              {`${approverObj.firstName || ''} ${approverObj.lastName || ''}`.trim()}
                            </option>
                          );
                        })()}
                        {managers.map((manager) => (
                          <option key={manager.contactId} value={manager.contactId}>
                            {manager.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Trade</div>
                      <div className="deficiency-detail-value">
                        {deficiency.tradeName || '-'}
                      </div>
                    </div>

                    <div className="deficiency-detail-item">
                      <div className="deficiency-detail-label">Approver</div>
                      <div className="deficiency-detail-value">
                        {typeof deficiency.approver === 'object' && deficiency.approver
                          ? `${deficiency.approver.firstName || ''} ${deficiency.approver.lastName || ''}`.trim()
                          : deficiency.approverName || '-'}
                      </div>
                    </div>
                  </>
                )}

                {editingAdditionalInfo ? (
                  <div className="form-group">
                    <label className="form-label">Cost Codes</label>
                    <input
                      type="text"
                      className="form-input"
                      value={deficiency.costCodes || ''}
                      onChange={(e) => handleInputChange('costCodes', e.target.value)}
                      placeholder="Enter cost codes"
                    />
                  </div>
                ) : (
                  <div className="deficiency-detail-item">
                    <div className="deficiency-detail-label">Cost Codes</div>
                    <div className="deficiency-detail-value">
                      {deficiency.costCodes || '-'}
                    </div>
                  </div>
                )}

                {editingAdditionalInfo ? (
                  <div className="form-group">
                    <label className="form-label">Private Notes</label>
                    <textarea
                      className="form-input"
                      value={deficiency.privateNotes || ''}
                      onChange={(e) => handleInputChange('privateNotes', e.target.value)}
                      placeholder="Enter private notes"
                      rows={4}
                    />
                  </div>
                ) : (
                  <div className="deficiency-detail-item">
                    <div className="deficiency-detail-label">Private Notes</div>
                    <div className="deficiency-detail-value" style={{ whiteSpace: 'pre-wrap' }}>
                      {deficiency.privateNotes || '-'}
                    </div>
                  </div>
                )}

                {editingAdditionalInfo && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E5E7EB' }}>
                    <button
                      onClick={() => setEditingAdditionalInfo(false)}
                      className="btn btn-secondary btn-sm"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdate}
                      className="btn btn-primary btn-sm"
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={16} />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div className="deficiency-section">
                <div className="deficiency-section-header">
                  <h3 className="deficiency-section-title">Attachments</h3>
                  {!editingAttachments && (
                    <button className="deficiency-section-edit-btn" onClick={() => setEditingAttachments(true)}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {/* Existing Attachments */}
                {deficiency.attachments && deficiency.attachments.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Current Attachments</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                      {deficiency.attachments.map((attachment, index) => {
                        const imageUrl = getImageUrl(attachment.url);
                        const hasPresignedUrl = attachmentUrls[attachment.url];

                        return (
                          <div
                            key={index}
                            style={{
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              padding: '8px',
                              position: 'relative'
                            }}
                          >
                            {hasPresignedUrl ? (
                              <img
                                key={imageUrl}
                                src={imageUrl}
                                alt={attachment.filename}
                                onClick={() => handleImageClick(imageUrl)}
                                onError={(e) => {
                                  console.error('Image failed to load:', attachment.url);
                                  e.currentTarget.style.display = 'none';
                                }}
                                style={{
                                  width: '100%',
                                  height: '100px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  marginBottom: '8px',
                                  cursor: 'pointer'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '100%',
                                height: '100px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '4px',
                                marginBottom: '8px'
                              }}>
                                <div className="deficiency-loading-spinner" style={{ width: '24px', height: '24px' }} />
                              </div>
                            )}
                            <div style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                              {attachment.filename}
                            </div>
                            {editingAttachments && (
                              <button
                                onClick={() => removeAttachment(index)}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* New Attachments in Edit Mode */}
                {editingAttachments && (
                  <>
                    {newAttachments.length > 0 && (
                      <div className="form-group">
                        <label className="form-label">New Attachments (Not uploaded yet)</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                          {newAttachments.map((file, index) => (
                            <div
                              key={index}
                              style={{
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '8px',
                                position: 'relative',
                                backgroundColor: '#f9f9f9'
                              }}
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                style={{
                                  width: '100%',
                                  height: '100px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  marginBottom: '8px'
                                }}
                              />
                              <div style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                                {file.name}
                              </div>
                              <button
                                onClick={() => removeNewAttachment(index)}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="form-group">
                      <label className="form-label">Add New Attachments</label>
                      <input
                        type="file"
                        id="attachment-input-details"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        multiple
                      />
                      <button
                        onClick={() => document.getElementById('attachment-input-details')?.click()}
                        className="btn btn-secondary btn-sm"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        <Upload size={16} />
                        Add Images
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E5E7EB' }}>
                      <button
                        onClick={() => {
                          setEditingAttachments(false);
                          setNewAttachments([]);
                        }}
                        className="btn btn-secondary btn-sm"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdate}
                        className="btn btn-primary btn-sm"
                        disabled={saving}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        <Save size={16} />
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </>
                )}
              </div>
              </div>

              {/* Tracking Information Section - Full Width */}
              <div className="deficiency-section" style={{ gridColumn: '1 / -1' }}>
                <div className="deficiency-section-header">
                  <h3 className="deficiency-section-title">Tracking Information</h3>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '20px',
                  marginTop: '16px'
                }}>
                  {/* Assigned Date */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Assigned Date
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.assignedDate ? new Date(deficiency.assignedDate).toLocaleDateString() : '-'}
                    </div>
                  </div>

                  {/* Completed By */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Completed By
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.completedBy && typeof deficiency.completedBy === 'object' && deficiency.completedBy.firstName && deficiency.completedBy.lastName
                        ? `${deficiency.completedBy.firstName} ${deficiency.completedBy.lastName}`
                        : deficiency.completedByName || '-'}
                    </div>
                  </div>

                  {/* Completed Date */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Completed Date
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.completedAt ? new Date(deficiency.completedAt).toLocaleDateString() : '-'}
                    </div>
                  </div>

                  {/* Opened By */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Opened By
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.openedBy && typeof deficiency.openedBy === 'object' && deficiency.openedBy.firstName && deficiency.openedBy.lastName
                        ? `${deficiency.openedBy.firstName} ${deficiency.openedBy.lastName}`
                        : deficiency.openedByName || '-'}
                    </div>
                  </div>

                  {/* Opened Date */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Opened Date
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.openedAt ? new Date(deficiency.openedAt).toLocaleDateString() : '-'}
                    </div>
                  </div>

                  {/* Closed By */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Closed By
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.closedBy && typeof deficiency.closedBy === 'object' && deficiency.closedBy.firstName && deficiency.closedBy.lastName
                        ? `${deficiency.closedBy.firstName} ${deficiency.closedBy.lastName}`
                        : deficiency.closedByName || '-'}
                    </div>
                  </div>

                  {/* Closed Date */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Closed Date
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.closedAt ? new Date(deficiency.closedAt).toLocaleDateString() : '-'}
                    </div>
                  </div>

                  {/* Reopened By */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Reopened By
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.reopenedBy && typeof deficiency.reopenedBy === 'object' && deficiency.reopenedBy.firstName && deficiency.reopenedBy.lastName
                        ? `${deficiency.reopenedBy.firstName} ${deficiency.reopenedBy.lastName}`
                        : deficiency.reopenedByName || '-'}
                    </div>
                  </div>

                  {/* Reopened Date */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Reopened Date
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.reopenedAt ? new Date(deficiency.reopenedAt).toLocaleDateString() : '-'}
                    </div>
                  </div>

                  {/* Updated By */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Updated By
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.updatedBy && typeof deficiency.updatedBy === 'object' && deficiency.updatedBy.firstName && deficiency.updatedBy.lastName
                        ? `${deficiency.updatedBy.firstName} ${deficiency.updatedBy.lastName}`
                        : deficiency.updatedByName || '-'}
                    </div>
                  </div>

                  {/* Updated Date */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Updated Date
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.updatedAt ? new Date(deficiency.updatedAt).toLocaleDateString() : '-'}
                    </div>
                  </div>

                  {/* Rejected */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Rejected
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.rejected ? 'Yes' : 'No'}
                    </div>
                  </div>

                  {deficiency.rejected && (
                    <>
                      {/* Rejected By */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Rejected By
                        </div>
                        <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                          {deficiency.rejectedBy && typeof deficiency.rejectedBy === 'object' && deficiency.rejectedBy.firstName && deficiency.rejectedBy.lastName
                            ? `${deficiency.rejectedBy.firstName} ${deficiency.rejectedBy.lastName}`
                            : '-'}
                        </div>
                      </div>

                      {/* Rejected Date */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Rejected Date
                        </div>
                        <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                          {deficiency.rejectedAt ? new Date(deficiency.rejectedAt).toLocaleDateString() : '-'}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Rejection Reason if exists */}
                {deficiency.rejected && deficiency.rejectedReason && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Rejection Reason
                    </div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {deficiency.rejectedReason}
                    </div>
                  </div>
                )}
              </div>

              {/* Related Deficiencies Section - Full Width */}
              <div className="deficiency-section" style={{ gridColumn: '1 / -1' }}>
                <div className="deficiency-section-header">
                  <h3 className="deficiency-section-title">Related Deficiencies</h3>
                  {!editingRelatedDeficiencies && (
                    <button className="deficiency-section-edit-btn" onClick={handleEditRelatedDeficiencies}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {editingRelatedDeficiencies ? (
                  <div>
                    {/* Edit Mode */}
                    <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowRelatedDeficiencyModal(true)}
                        disabled={!deficiency?.projectId && !projectId}
                      >
                        Add Related Deficiency
                      </button>
                    </div>

                    {/* Display selected items in edit mode */}
                    {tempRelatedDeficiencies.length === 0 ? (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#9CA3AF',
                        fontSize: '14px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '6px'
                      }}>
                        No related deficiencies added
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                        {tempRelatedDeficiencies.map((item, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '12px',
                              backgroundColor: '#F9FAFB',
                              border: '1px solid #E5E7EB',
                              borderRadius: '6px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span style={{ fontSize: '14px', color: '#48182F', fontWeight: '500' }}>
                                {item.deficiencyNumber ? `#${item.deficiencyNumber}` : ''} {item.title || 'Deficiency'}
                              </span>
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                                {item.relationshipType === 'duplicate' ? 'Duplicate' : 'Related'}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveRelatedDeficiency(index)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#EF4444'
                              }}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Save/Cancel buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSaveRelatedDeficienciesChanges}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleCancelRelatedDeficienciesEdit}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* View Mode */}
                    {!deficiency.relatedDeficiencies || deficiency.relatedDeficiencies.length === 0 ? (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#9CA3AF',
                        fontSize: '14px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '6px',
                        marginTop: '16px'
                      }}>
                        No related deficiencies
                      </div>
                    ) : (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '16px',
                        marginTop: '16px'
                      }}>
                        {deficiency.relatedDeficiencies.map((item, index) => (
                          <div
                            key={index}
                            style={{
                              backgroundColor: '#F9FAFB',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              padding: '16px',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => {
                              // Open in new tab
                              const currentUrl = new URL(window.location.href);
                              currentUrl.searchParams.set('view', item.deficiency._id);
                              window.open(currentUrl.toString(), '_blank');
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                              e.currentTarget.style.borderColor = '#D1D5DB';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.borderColor = '#E5E7EB';
                            }}
                          >
                            {/* Relationship Type Badge */}
                            <div style={{ marginBottom: '12px' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                backgroundColor: item.relationshipType === 'duplicate' ? '#FEF3C7' : '#DBEAFE',
                                color: item.relationshipType === 'duplicate' ? '#92400E' : '#1E40AF'
                              }}>
                                {item.relationshipType === 'duplicate' ? 'Duplicate' : 'Related'}
                              </span>
                            </div>

                            {/* Deficiency Number and Title on same line */}
                            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                              <span style={{
                                fontSize: '18px',
                                fontWeight: '700',
                                color: '#48182F',
                                flexShrink: 0
                              }}>
                                #{item.deficiency.deficiencyNumber}
                              </span>
                              <span style={{
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#111827',
                                lineHeight: '1.5',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.deficiency.title}
                              </span>
                            </div>

                            {/* Due Date and Status */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              paddingTop: '12px',
                              borderTop: '1px solid #E5E7EB'
                            }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', textTransform: 'uppercase' }}>
                                  Due Date
                                </span>
                                <span style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                                  {new Date(item.deficiency.dueDate).toLocaleDateString()}
                                </span>
                              </div>
                              <div>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  backgroundColor:
                                    item.deficiency.status === 'open' ? '#FEF3C7' :
                                    item.deficiency.status === 'assigned' ? '#DBEAFE' :
                                    item.deficiency.status === 'accepted' ? '#D1FAE5' :
                                    item.deficiency.status === 'not-accepted' ? '#FEE2E2' :
                                    item.deficiency.status === 'completed' ? '#D1FAE5' :
                                    item.deficiency.status === 'closed' ? '#E5E7EB' :
                                    item.deficiency.status === 'reopened' ? '#FEF3C7' : '#F3F4F6',
                                  color:
                                    item.deficiency.status === 'open' ? '#92400E' :
                                    item.deficiency.status === 'assigned' ? '#1E40AF' :
                                    item.deficiency.status === 'accepted' ? '#065F46' :
                                    item.deficiency.status === 'not-accepted' ? '#DC2626' :
                                    item.deficiency.status === 'completed' ? '#065F46' :
                                    item.deficiency.status === 'closed' ? '#374151' :
                                    item.deficiency.status === 'reopened' ? '#92400E' : '#6B7280'
                                }}>
                                  {item.deficiency.status.charAt(0).toUpperCase() + item.deficiency.status.slice(1).replace('-', ' ')}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Comments Section - Full Width at Bottom */}
              <div className="deficiency-section" style={{ gridColumn: '1 / -1' }}>
                <div className="deficiency-section-header">
                  <h3 className="deficiency-section-title">Comments <span style={{ fontSize: '12px', color: '#059669', fontWeight: 500 }}>( Info: Purchaser can see )</span></h3>
                </div>

                {/* Existing Comments */}
                {deficiency.comments && deficiency.comments.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    {deficiency.comments.map((comment) => (
                      <div
                        key={comment._id}
                        style={{
                          background: '#F9FAFB',
                          borderRadius: '8px',
                          padding: '16px',
                          marginBottom: '12px',
                          border: '1px solid #E5E7EB'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                          <div>
                            <span style={{ fontWeight: 600, color: '#48182F', marginRight: '8px' }}>
                              {comment.createdByName}
                            </span>
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>
                              {formatCommentDate(comment.createdAt)}
                            </span>
                          </div>
                        </div>
                        <div style={{ color: '#374151', fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                            {comment.attachments.map((attachment, idx) => (
                              <img
                                key={idx}
                                src={getImageUrl(attachment.url)}
                                alt={attachment.filename}
                                onClick={() => handleImageClick(getImageUrl(attachment.url))}
                                style={{
                                  width: '80px',
                                  height: '80px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  border: '1px solid #E5E7EB'
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Comment Form */}
                <div style={{ background: '#FFFFFF', borderRadius: '8px', border: '1px solid #D1D5DB', padding: '16px' }}>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Type message..."
                    disabled={submittingComment}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      marginBottom: '12px',
                      boxSizing: 'border-box'
                    }}
                  />

                  {/* Selected Images Preview */}
                  {commentImages.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      {commentImages.map((image, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img
                            src={URL.createObjectURL(image)}
                            alt={image.name}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #D1D5DB'
                            }}
                          />
                          <button
                            onClick={() => removeCommentImage(idx)}
                            style={{
                              position: 'absolute',
                              top: '-6px',
                              right: '-6px',
                              background: '#DC2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleCommentImageSelect}
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={submittingComment}
                      className="btn btn-secondary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      📎 Attach Image
                    </button>
                    <button
                      onClick={handleSubmitComment}
                      disabled={submittingComment || (!commentText.trim() && commentImages.length === 0)}
                      className="btn btn-primary btn-sm"
                    >
                      {submittingComment ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Internal Comments Section - Full Width */}
              <div className="deficiency-section" style={{ gridColumn: '1 / -1' }}>
                <div className="deficiency-section-header">
                  <h3 className="deficiency-section-title">Internal Comments <span style={{ fontSize: '12px', color: '#DC2626', fontWeight: 500 }}>( Info: Purchaser can't see )</span></h3>
                </div>

                {/* Existing Internal Comments */}
                {deficiency.internalComments && deficiency.internalComments.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    {deficiency.internalComments.map((comment: Comment) => (
                      <div
                        key={comment._id}
                        style={{
                          background: '#F9FAFB',
                          borderRadius: '8px',
                          padding: '16px',
                          marginBottom: '12px',
                          border: '1px solid #E5E7EB'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                          <div>
                            <span style={{ fontWeight: 600, color: '#48182F', marginRight: '8px' }}>
                              {comment.createdByName}
                            </span>
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>
                              {formatCommentDate(comment.createdAt)}
                            </span>
                          </div>
                        </div>
                        <div style={{ color: '#374151', fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                            {comment.attachments.map((attachment: { url: string; filename: string }, idx: number) => (
                              <img
                                key={idx}
                                src={getImageUrl(attachment.url)}
                                alt={attachment.filename}
                                onClick={() => handleImageClick(getImageUrl(attachment.url))}
                                style={{
                                  width: '80px',
                                  height: '80px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  border: '1px solid #E5E7EB'
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Internal Comment Form */}
                <div style={{ background: '#FFFFFF', borderRadius: '8px', border: '1px solid #D1D5DB', padding: '16px' }}>
                  <textarea
                    value={internalCommentText}
                    onChange={(e) => setInternalCommentText(e.target.value)}
                    placeholder="Type internal message..."
                    disabled={submittingInternalComment}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      marginBottom: '12px',
                      boxSizing: 'border-box'
                    }}
                  />

                  {/* Selected Images Preview */}
                  {internalCommentImages.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      {internalCommentImages.map((image, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img
                            src={URL.createObjectURL(image)}
                            alt={image.name}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #D1D5DB'
                            }}
                          />
                          <button
                            onClick={() => removeInternalCommentImage(idx)}
                            style={{
                              position: 'absolute',
                              top: '-6px',
                              right: '-6px',
                              background: '#DC2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      ref={internalFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleInternalCommentImageSelect}
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => internalFileInputRef.current?.click()}
                      disabled={submittingInternalComment}
                      className="btn btn-secondary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      📎 Attach Image
                    </button>
                    <button
                      onClick={handleSubmitInternalComment}
                      disabled={submittingInternalComment || (!internalCommentText.trim() && internalCommentImages.length === 0)}
                      className="btn btn-primary btn-sm"
                    >
                      {submittingInternalComment ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Change History FAB Button */}
        <ChangeHistoryButton
          onClick={() => setHistoryDrawerOpen(true)}
          isOpen={historyDrawerOpen}
        />
      </div>

      {/* Change History Drawer */}
      <ChangeHistoryDrawer
        isOpen={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        entityType="deficiency"
        entityId={deficiencyId}
        workspaceId={workspaceId}
      />

      {/* Image Editor Modal */}
      {currentImageFile && (
        <ImageEditor
          isOpen={imageEditorOpen}
          onClose={() => {
            setImageEditorOpen(false);
            setCurrentImageFile(null);
          }}
          imageFile={currentImageFile}
          onSave={handleImageEditorSave}
        />
      )}

      {/* Image Preview Modal */}
      {imageModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={handleCloseImageModal}
        >
          <button
            onClick={handleCloseImageModal}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '24px',
              color: '#333',
              zIndex: 2001
            }}
          >
            ×
          </button>
          <img
            src={selectedImageUrl}
            alt="Full size preview"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
          />
        </div>
      )}

      {/* Related Deficiency Modal */}
      {showRelatedDeficiencyModal && deficiency && (
        <RelatedDeficiencyModal
          isOpen={showRelatedDeficiencyModal}
          onClose={() => setShowRelatedDeficiencyModal(false)}
          onSave={handleSaveRelatedDeficiencies}
          workspaceId={workspaceId}
          projectId={deficiency.projectId || projectId}
          unitId={deficiency.scopeType === 'units' && deficiency.unit ? (typeof deficiency.unit === 'string' ? deficiency.unit : deficiency.unit._id) : undefined}
          commonAreaId={deficiency.scopeType === 'commonArea' && deficiency.commonArea ? (typeof deficiency.commonArea === 'string' ? deficiency.commonArea : deficiency.commonArea._id) : undefined}
          existingRelatedIds={tempRelatedDeficiencies.map(item => item.deficiency)}
          currentDeficiencyId={deficiency._id}
        />
      )}
    </>
  );
};

export default DeficiencyDetailsSlider;
